import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/adminlayout';
import { apiGet, apiPost, apiPut, apiDelete } from '../../utils/api';
import { PRODUCTION_URL } from '../productionLink/productionLink';

const FrontOfficeOperations = () => {
  const [loading, setLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [dashboardData, setDashboardData] = useState({
    roomStats: {
      totalRooms: 0,
      available: 0,
      occupied: 0,
      cleaning: 0,
      maintenance: 0,
      outOfOrder: 0
    },
    todayArrivals: [],
    todayDepartures: [],
    pendingPayments: []
  });
  const [filterArrivals, setFilterArrivals] = useState('all');
  const [filterCheckouts, setFilterCheckouts] = useState('all');
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');

  const colors = {
    primary: '#2D5016',
    accent: '#6ba83e',
    lightGray: '#f8faf9',
    mediumGray: '#e8ede9',
    textDark: '#1a1a1a',
    textLight: '#6b7280',
    white: '#ffffff',
    success: '#4a7c2a',
    warning: '#f59e0b',
    danger: '#ef4444',
    info: '#3b82f6',
    purple: '#8b5cf6'
  };

  const isMobile = windowWidth <= 768;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 120000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch room statistics
      const statsData = await apiGet('/admin/stats');

      // Fetch all rooms to get detailed status counts
      const roomsData = await apiGet('/admin/rooms');

      // Calculate room status counts
      const roomCounts = {
        totalRooms: roomsData.data.length,
        available: roomsData.data.filter(r => r.status === 'available').length,
        occupied: roomsData.data.filter(r => r.status === 'occupied').length,
        cleaning: roomsData.data.filter(r => r.status === 'cleaning').length,
        maintenance: roomsData.data.filter(r => r.status === 'maintenance').length,
        outOfOrder: roomsData.data.filter(r => r.status === 'out_of_order').length
      };

      // Fetch today's arrivals and departures
      const todayData = await apiGet('/admin/front-office/operations/today');

      // Fetch current guests for pending payments
      const guestsData = await apiGet('/admin/stay-overview/current-guests');

      // Filter guests with pending payments
      const pendingPayments = guestsData.data.filter(g => g.pending_amount > 0).map(guest => ({
        ...guest,
        days_overdue: Math.floor((new Date() - new Date(guest.booking_date || guest.created_at)) / (1000 * 60 * 60 * 24))
      }));

      setDashboardData({
        roomStats: roomCounts,
        todayArrivals: todayData.data?.arrivals || [],
        todayDepartures: todayData.data?.departures || [],
        pendingPayments: pendingPayments
      });

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
    setLoading(false);
  };

  const handleQuickCheckIn = async (bookingId) => {
    try {
      const data = await apiPost('/admin/front-office/operations/quick-checkin/${bookingId}', {});
      if (data.success) {
        setSuccessMessage('✅ Guest checked in successfully!');
        fetchDashboardData();
      }
    } catch (error) {
      console.error('Error during check-in:', error);
    }
  };

  const handleQuickCheckOut = async (bookingId) => {
    try {
      const data = await apiPost('/admin/stay-overview/checkout/${bookingId}', {});
      if (data.success) {
        setSuccessMessage('✅ Guest checked out successfully!');
        fetchDashboardData();
      } else {
        setSuccessMessage(`❌ ${data.message}`);
      }
    } catch (error) {
      console.error('Error during check-out:', error);
      setSuccessMessage('❌ Error during checkout');
    }
  };

  const handleRecordPayment = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    try {
      const response = await fetch(`${PRODUCTION_URL}/api/admin/stay-overview/add-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bookingId: selectedGuest.id,
          amount: parseFloat(paymentAmount),
          paymentMethod: paymentMethod
        })
      });
      const data = await response.json();
      if (data.success) {
        setSuccessMessage('✅ Payment recorded successfully!');
        setShowPaymentModal(false);
        setPaymentAmount('');
        setSelectedGuest(null);
        fetchDashboardData();
      }
    } catch (error) {
      console.error('Error recording payment:', error);
      setSuccessMessage('❌ Error recording payment');
    }
  };

  const filteredArrivals = dashboardData.todayArrivals.filter(arrival => {
    if (filterArrivals === 'all') return true;
    if (filterArrivals === 'channel') return arrival.booking_type === 'channel_manager';
    if (filterArrivals === 'direct') return arrival.booking_type === 'direct';
    return true;
  });

  const filteredCheckouts = dashboardData.todayDepartures;

  const styles = {
    pageHeader: {
      marginBottom: isMobile ? '20px' : '28px',
      padding: isMobile ? '0 8px' : '0'
    },
    pageTitle: {
      fontSize: isMobile ? '22px' : isTablet ? '26px' : '32px',
      fontWeight: '800',
      color: colors.textDark,
      marginBottom: '8px',
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent'
    },
    pageSubtitle: {
      fontSize: isMobile ? '13px' : '15px',
      color: colors.textLight,
      fontWeight: '500'
    },
    refreshButton: {
      marginTop: '12px',
      padding: isMobile ? '8px 16px' : '10px 20px',
      background: colors.primary,
      color: colors.white,
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: isMobile ? '12px' : '14px',
      fontWeight: '600',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.3s ease',
      boxShadow: '0 2px 8px rgba(45, 80, 22, 0.2)'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr 1fr' : isTablet ? 'repeat(3, 1fr)' : 'repeat(6, 1fr)',
      gap: isMobile ? '12px' : '16px',
      marginBottom: isMobile ? '24px' : '32px',
      padding: isMobile ? '0 8px' : '0'
    },
    statCard: {
      background: colors.white,
      padding: isMobile ? '16px' : '20px',
      borderRadius: isMobile ? '14px' : '16px',
      boxShadow: '0 4px 16px rgba(45, 80, 22, 0.08)',
      border: `2px solid ${colors.mediumGray}`,
      transition: 'all 0.3s ease',
      cursor: 'pointer'
    },
    statValue: {
      fontSize: isMobile ? '28px' : '36px',
      fontWeight: '800',
      marginBottom: '6px',
      lineHeight: '1'
    },
    statLabel: {
      fontSize: isMobile ? '10px' : '11px',
      color: colors.textLight,
      textTransform: 'uppercase',
      fontWeight: '700',
      letterSpacing: '0.8px'
    },
    statIcon: {
      fontSize: isMobile ? '20px' : '24px',
      marginBottom: '8px',
      display: 'block'
    },
    contentSection: {
      marginBottom: isMobile ? '20px' : '28px'
    },
    sectionCard: {
      background: colors.white,
      padding: isMobile ? '18px' : '24px',
      borderRadius: isMobile ? '14px' : '18px',
      boxShadow: '0 8px 24px rgba(45, 80, 22, 0.1)',
      border: `1px solid ${colors.mediumGray}`,
      margin: isMobile ? '0 8px' : '0'
    },
    sectionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: isMobile ? '16px' : '20px',
      flexWrap: 'wrap',
      gap: '12px'
    },
    sectionTitle: {
      fontSize: isMobile ? '18px' : '22px',
      fontWeight: '700',
      color: colors.textDark,
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    filterContainer: {
      display: 'flex',
      gap: '8px',
      flexWrap: 'wrap'
    },
    filterButton: {
      padding: isMobile ? '6px 12px' : '8px 16px',
      borderRadius: '8px',
      border: `1.5px solid ${colors.mediumGray}`,
      background: colors.white,
      fontSize: isMobile ? '11px' : '13px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      color: colors.textDark
    },
    activeFilter: {
      background: colors.primary,
      color: colors.white,
      borderColor: colors.primary
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: isMobile ? '12px' : '14px'
    },
    tableHeader: {
      background: colors.lightGray,
      fontWeight: '700',
      textAlign: 'left',
      padding: isMobile ? '12px' : '14px',
      borderBottom: `2px solid ${colors.mediumGray}`,
      color: colors.textDark,
      fontSize: isMobile ? '11px' : '13px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    tableCell: {
      padding: isMobile ? '12px' : '14px',
      borderBottom: `1px solid ${colors.lightGray}`,
      verticalAlign: 'middle'
    },
    badge: {
      padding: '5px 12px',
      borderRadius: '6px',
      fontSize: '11px',
      fontWeight: '700',
      display: 'inline-block',
      textTransform: 'uppercase',
      letterSpacing: '0.3px'
    },
    button: {
      padding: isMobile ? '7px 14px' : '9px 18px',
      borderRadius: '7px',
      border: 'none',
      fontSize: isMobile ? '11px' : '13px',
      fontWeight: '700',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      textTransform: 'uppercase',
      letterSpacing: '0.3px'
    },
    primaryButton: {
      background: colors.primary,
      color: colors.white,
      boxShadow: '0 2px 8px rgba(45, 80, 22, 0.3)'
    },
    successButton: {
      background: colors.success,
      color: colors.white,
      boxShadow: '0 2px 8px rgba(74, 124, 42, 0.3)'
    },
    dangerButton: {
      background: colors.danger,
      color: colors.white,
      boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)'
    },
    infoButton: {
      background: colors.info,
      color: colors.white,
      boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)'
    },
    emptyState: {
      textAlign: 'center',
      padding: isMobile ? '40px 20px' : '60px 40px',
      color: colors.textLight,
      fontSize: isMobile ? '14px' : '16px'
    },
    emptyStateIcon: {
      fontSize: isMobile ? '48px' : '64px',
      marginBottom: '16px',
      opacity: 0.5
    },
    successAlert: {
      position: 'fixed',
      top: '20px',
      right: '20px',
      background: successMessage.includes('❌') ? colors.danger : colors.success,
      color: colors.white,
      padding: '16px 24px',
      borderRadius: '12px',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
      fontSize: '14px',
      fontWeight: '600',
      zIndex: 10000,
      animation: 'slideIn 0.3s ease'
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '20px'
    },
    modalContent: {
      background: colors.white,
      borderRadius: '20px',
      padding: isMobile ? '24px' : '32px',
      maxWidth: '600px',
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
    },
    loadingOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(255, 255, 255, 0.9)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9998
    },
    input: {
      width: '100%',
      padding: '12px',
      borderRadius: '8px',
      border: `1.5px solid ${colors.mediumGray}`,
      fontSize: '14px',
      marginBottom: '16px',
      fontFamily: 'inherit'
    },
    select: {
      width: '100%',
      padding: '12px',
      borderRadius: '8px',
      border: `1.5px solid ${colors.mediumGray}`,
      fontSize: '14px',
      marginBottom: '16px',
      fontFamily: 'inherit',
      cursor: 'pointer'
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={styles.loadingOverlay}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
            <div style={{ fontSize: '18px', fontWeight: '600', color: colors.primary }}>
              Loading Dashboard...
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {successMessage && (
        <div style={styles.successAlert}>
          {successMessage}
        </div>
      )}

      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>🏨 Front Office Operations</h1>
        <p style={styles.pageSubtitle}>
          Real-time operations dashboard • {new Date().toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
        </p>
        <button
          onClick={fetchDashboardData}
          style={styles.refreshButton}
          onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
          onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
        >
          <span>🔄</span> Refresh Data
        </button>
      </div>

      {/* Room Status Overview */}
      <div style={styles.statsGrid}>
        <div
          style={{
            ...styles.statCard,
            borderColor: colors.primary,
            borderWidth: '2px'
          }}
          onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{ ...styles.statIcon, color: colors.primary }}>🏠</div>
          <div style={{ ...styles.statValue, color: colors.primary }}>
            {dashboardData.roomStats.totalRooms}
          </div>
          <div style={styles.statLabel}>Total Rooms</div>
        </div>

        <div
          style={{
            ...styles.statCard,
            borderColor: colors.success,
            borderWidth: '2px'
          }}
          onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{ ...styles.statIcon, color: colors.success }}>✅</div>
          <div style={{ ...styles.statValue, color: colors.success }}>
            {dashboardData.roomStats.available}
          </div>
          <div style={styles.statLabel}>Available</div>
        </div>

        <div
          style={{
            ...styles.statCard,
            borderColor: colors.danger,
            borderWidth: '2px'
          }}
          onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{ ...styles.statIcon, color: colors.danger }}>🛏️</div>
          <div style={{ ...styles.statValue, color: colors.danger }}>
            {dashboardData.roomStats.occupied}
          </div>
          <div style={styles.statLabel}>Occupied</div>
        </div>

        <div
          style={{
            ...styles.statCard,
            borderColor: colors.warning,
            borderWidth: '2px'
          }}
          onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{ ...styles.statIcon, color: colors.warning }}>🧹</div>
          <div style={{ ...styles.statValue, color: colors.warning }}>
            {dashboardData.roomStats.cleaning}
          </div>
          <div style={styles.statLabel}>Cleaning</div>
        </div>

        <div
          style={{
            ...styles.statCard,
            borderColor: colors.info,
            borderWidth: '2px'
          }}
          onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{ ...styles.statIcon, color: colors.info }}>🔧</div>
          <div style={{ ...styles.statValue, color: colors.info }}>
            {dashboardData.roomStats.maintenance}
          </div>
          <div style={styles.statLabel}>Maintenance</div>
        </div>

        <div
          style={{
            ...styles.statCard,
            borderColor: colors.purple,
            borderWidth: '2px'
          }}
          onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-4px)'}
          onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{ ...styles.statIcon, color: colors.purple }}>⚠️</div>
          <div style={{ ...styles.statValue, color: colors.purple }}>
            {dashboardData.roomStats.outOfOrder}
          </div>
          <div style={styles.statLabel}>Out of Order</div>
        </div>
      </div>


      {/* Guest Details Modal */}
      {showDetailsModal && selectedGuest && (
        <div style={styles.modal} onClick={() => setShowDetailsModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={{
              fontSize: isMobile ? '20px' : '24px',
              marginBottom: '24px',
              fontWeight: '800',
              color: colors.primary
            }}>
              Guest Details
            </h3>

            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '12px', color: colors.textLight, marginBottom: '6px', fontWeight: '600' }}>
                Guest Name
              </div>
              <div style={{ fontSize: '18px', fontWeight: '700' }}>
                {selectedGuest.guest_name}
              </div>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              gap: '20px',
              marginBottom: '20px'
            }}>
              <div>
                <div style={{ fontSize: '12px', color: colors.textLight, marginBottom: '6px', fontWeight: '600' }}>
                  Phone Number
                </div>
                <div style={{ fontSize: '16px', fontWeight: '600' }}>
                  {selectedGuest.guest_phone}
                </div>
              </div>
              {selectedGuest.guest_email && (
                <div>
                  <div style={{ fontSize: '12px', color: colors.textLight, marginBottom: '6px', fontWeight: '600' }}>
                    Email Address
                  </div>
                  <div style={{ fontSize: '16px', fontWeight: '600' }}>
                    {selectedGuest.guest_email}
                  </div>
                </div>
              )}
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              gap: '20px',
              marginBottom: '20px'
            }}>
              <div>
                <div style={{ fontSize: '12px', color: colors.textLight, marginBottom: '6px', fontWeight: '600' }}>
                  Room Number
                </div>
                <div style={{ fontSize: '16px', fontWeight: '600' }}>
                  {selectedGuest.room_number}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '12px', color: colors.textLight, marginBottom: '6px', fontWeight: '600' }}>
                  Room Type
                </div>
                <div style={{ fontSize: '16px', fontWeight: '600' }}>
                  {selectedGuest.room_type}
                </div>
              </div>
            </div>

            {selectedGuest.check_in_date && selectedGuest.check_out_date && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                gap: '20px',
                marginBottom: '20px'
              }}>
                <div>
                  <div style={{ fontSize: '12px', color: colors.textLight, marginBottom: '6px', fontWeight: '600' }}>
                    Check-in Date
                  </div>
                  <div style={{ fontSize: '16px', fontWeight: '600' }}>
                    {new Date(selectedGuest.check_in_date).toLocaleDateString()}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: colors.textLight, marginBottom: '6px', fontWeight: '600' }}>
                    Check-out Date
                  </div>
                  <div style={{ fontSize: '16px', fontWeight: '600' }}>
                    {new Date(selectedGuest.check_out_date).toLocaleDateString()}
                  </div>
                </div>
              </div>
            )}

            <div style={{
              background: colors.lightGray,
              padding: '20px',
              borderRadius: '12px',
              marginBottom: '20px'
            }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
                gap: '16px'
              }}>
                <div>
                  <div style={{ fontSize: '11px', color: colors.textLight, marginBottom: '4px', fontWeight: '600' }}>
                    Total Amount
                  </div>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: colors.primary }}>
                    ₹{parseFloat(selectedGuest.total_amount).toLocaleString('en-IN')}
                  </div>
                </div>
                {selectedGuest.total_paid !== undefined && (
                  <div>
                    <div style={{ fontSize: '11px', color: colors.textLight, marginBottom: '4px', fontWeight: '600' }}>
                      Paid Amount
                    </div>
                    <div style={{ fontSize: '18px', fontWeight: '700', color: colors.success }}>
                      ₹{parseFloat(selectedGuest.total_paid || 0).toLocaleString('en-IN')}
                    </div>
                  </div>
                )}
                {selectedGuest.pending_amount !== undefined && selectedGuest.pending_amount > 0 && (
                  <div>
                    <div style={{ fontSize: '11px', color: colors.textLight, marginBottom: '4px', fontWeight: '600' }}>
                      Pending Amount
                    </div>
                    <div style={{ fontSize: '18px', fontWeight: '700', color: colors.danger }}>
                      ₹{parseFloat(selectedGuest.pending_amount).toLocaleString('en-IN')}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <button
              onClick={() => setShowDetailsModal(false)}
              style={{
                ...styles.button,
                ...styles.primaryButton,
                width: '100%',
                padding: '14px',
                fontSize: '14px'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedGuest && (
        <div style={styles.modal} onClick={() => setShowPaymentModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={{
              fontSize: isMobile ? '20px' : '24px',
              marginBottom: '24px',
              fontWeight: '800',
              color: colors.primary
            }}>
              Record Payment
            </h3>

            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '14px', color: colors.textLight, marginBottom: '8px' }}>
                Guest: <strong>{selectedGuest.guest_name}</strong>
              </div>
              <div style={{ fontSize: '14px', color: colors.textLight, marginBottom: '8px' }}>
                Room: <strong>{selectedGuest.room_number}</strong>
              </div>
              <div style={{ fontSize: '14px', color: colors.danger, marginBottom: '8px' }}>
                Pending Amount: <strong>₹{parseFloat(selectedGuest.pending_amount).toLocaleString('en-IN')}</strong>
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '8px', color: colors.textDark }}>
                Payment Amount
              </label>
              <input
                type="number"
                placeholder="Enter amount"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                style={styles.input}
                max={selectedGuest.pending_amount}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '8px', color: colors.textDark }}>
                Payment Method
              </label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                style={styles.select}
              >
                <option value="cash">Cash</option>
                <option value="card">Card</option>
                <option value="upi">UPI</option>
                <option value="bank_transfer">Bank Transfer</option>
              </select>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleRecordPayment}
                style={{
                  ...styles.button,
                  ...styles.successButton,
                  flex: 1,
                  padding: '14px',
                  fontSize: '14px'
                }}
              >
                Record Payment
              </button>
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setPaymentAmount('');
                  setSelectedGuest(null);
                }}
                style={{
                  ...styles.button,
                  background: colors.mediumGray,
                  color: colors.textDark,
                  flex: 1,
                  padding: '14px',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default FrontOfficeOperations;